// TOREFACTO :
// - Split this file to subComponents
// - Split all config inside helpers or constants
import {
  KdsButton,
  KdsButtonIcon,
  KdsText,
} from "@kazaar-platform/design-system";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiFlashlightFill,
} from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { sortBy, uniq } from "lodash";
import { useMemo, useState } from "react";
import {
  type OrderItemDetailsReadDto,
  WorkflowPostponedActivityReadDtoActivityEnum,
} from "@/config/apiClient";
import { FeatureEnum } from "@/config/apiClient.types";
import { formatOrderItemStepsFromHistory } from "@/helpers/workflows";
import {
  type WithOrderItemActionsComponentProps,
  withOrderItemActions,
} from "@/hoc/withOrderItemActions";
import { useGetIsFeatureActive } from "@/hooks";
import { useIsOMS } from "@/hooks/useIsOMS";
import { OrderItemCSMPreMediaSection } from "./OrderItemCSMPreMediaSection";
import { ProofValidationActions } from "./ProofValidationActions";
import { QuoteValidationActions } from "./QuoteValidationActions/QuoteValidationActions";

const activitiesOrder: WorkflowPostponedActivityReadDtoActivityEnum[] = [
  WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief,
  WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief,
  WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition,
  WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition,
  WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition,
  WorkflowPostponedActivityReadDtoActivityEnum.AddQuote,
  WorkflowPostponedActivityReadDtoActivityEnum.SendQuote,
  WorkflowPostponedActivityReadDtoActivityEnum.DecideOnQuote,
  WorkflowPostponedActivityReadDtoActivityEnum.AddHdf,
  WorkflowPostponedActivityReadDtoActivityEnum.AddProof,
  WorkflowPostponedActivityReadDtoActivityEnum.DecideOnProof,
  WorkflowPostponedActivityReadDtoActivityEnum.AddPurchaseOrder,
  WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote,
  WorkflowPostponedActivityReadDtoActivityEnum.ShareRetroPlanning,
  WorkflowPostponedActivityReadDtoActivityEnum.CheckRetroPlanning,
];

type ActivityKey = WorkflowPostponedActivityReadDtoActivityEnum | "created";

const OrderItemStatusComponent = ({
  actions,
  orderItem,
  isLoading,
  onError,
}: WithOrderItemActionsComponentProps) => {
  // State
  const [selectedPostponedActivity, setSelectedPostponedActivity] = useState<
    WorkflowPostponedActivityReadDtoActivityEnum | undefined
  >();

  // Hooks
  const { t } = useTranslate();
  const isOMS = useIsOMS();

  // Translation maps for activities
  const activityTitleMap: Partial<Record<ActivityKey, string>> = {
    [WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote]: t(
      "orderItemStatus.info.title.ADD_DELIVERY_NOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddHdf]: t(
      "orderItemStatus.info.title.ADD_HDF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief]: t(
      "orderItemStatus.info.title.ADD_PRE_MEDIA_BRIEF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition]: t(
      "orderItemStatus.info.title.ADD_PRE_MEDIA_PROPOSITION"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddProof]: t(
      "orderItemStatus.info.title.ADD_PROOF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPurchaseOrder]: t(
      "orderItemStatus.info.title.ADD_PURCHASE_ORDER"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddQuote]: t(
      "orderItemStatus.info.title.ADD_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.CheckRetroPlanning]: t(
      "orderItemStatus.info.title.CHECK_RETRO_PLANNING"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief]: t(
      "orderItemStatus.info.title.DECIDE_ON_PRE_MEDIA_BRIEF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition]:
      t("orderItemStatus.info.title.DECIDE_ON_PRE_MEDIA_PROPOSITION"),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnProof]: t(
      "orderItemStatus.info.title.DECIDE_ON_PROOF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnQuote]: t(
      "orderItemStatus.info.title.DECIDE_ON_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition]:
      t("orderItemStatus.info.title.PROCESS_PRE_MEDIA_PROPOSITION"),
    [WorkflowPostponedActivityReadDtoActivityEnum.SendQuote]: t(
      "orderItemStatus.info.title.SEND_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.ShareRetroPlanning]: t(
      "orderItemStatus.info.title.SHARE_RETRO_PLANNING"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryLabel]: t(
      "orderItemStatus.info.title.ADD_DELIVERY_LABEL"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnDeliveryLabel]: t(
      "orderItemStatus.info.title.DECIDE_ON_DELIVERY_LABEL"
    ),
  };

  const activityDescriptionMap: Partial<Record<ActivityKey, string>> = {
    [WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote]: t(
      "orderItemStatus.info.description.ADD_DELIVERY_NOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddHdf]: t(
      "orderItemStatus.info.description.ADD_HDF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief]: t(
      "orderItemStatus.info.description.ADD_PRE_MEDIA_BRIEF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition]: t(
      "orderItemStatus.info.description.ADD_PRE_MEDIA_PROPOSITION"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddProof]: t(
      "orderItemStatus.info.description.ADD_PROOF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPurchaseOrder]: t(
      "orderItemStatus.info.description.ADD_PURCHASE_ORDER"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddQuote]: t(
      "orderItemStatus.info.description.ADD_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.CheckRetroPlanning]: t(
      "orderItemStatus.info.description.CHECK_RETRO_PLANNING"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest]:
      t("orderItemStatus.info.description.DECIDE_ON_MODIFICATION_REQUEST"),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief]: t(
      "orderItemStatus.info.description.DECIDE_ON_PRE_MEDIA_BRIEF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition]:
      t("orderItemStatus.info.description.DECIDE_ON_PRE_MEDIA_PROPOSITION"),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnProof]: t(
      "orderItemStatus.info.description.DECIDE_ON_PROOF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnQuote]: t(
      "orderItemStatus.info.description.DECIDE_ON_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition]:
      t("orderItemStatus.info.description.PROCESS_PRE_MEDIA_PROPOSITION"),
    [WorkflowPostponedActivityReadDtoActivityEnum.SendQuote]: t(
      "orderItemStatus.info.description.SEND_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.ShareRetroPlanning]: t(
      "orderItemStatus.info.description.SHARE_RETRO_PLANNING"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryLabel]: t(
      "orderItemStatus.info.description.ADD_DELIVERY_LABEL"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnDeliveryLabel]: t(
      "orderItemStatus.info.description.DECIDE_ON_DELIVERY_LABEL"
    ),
  };

  const activityCtaMap: Partial<Record<ActivityKey, string>> = {
    [WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote]: t(
      "orderItemStatus.info.cta.ADD_DELIVERY_NOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddHdf]: t(
      "orderItemStatus.info.cta.ADD_HDF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief]: t(
      "orderItemStatus.info.cta.ADD_PRE_MEDIA_BRIEF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition]: t(
      "orderItemStatus.info.cta.ADD_PRE_MEDIA_PROPOSITION"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddProof]: t(
      "orderItemStatus.info.cta.ADD_PROOF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddPurchaseOrder]: t(
      "orderItemStatus.info.cta.ADD_PURCHASE_ORDER"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddQuote]: t(
      "orderItemStatus.info.cta.ADD_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.CheckRetroPlanning]: t(
      "orderItemStatus.info.cta.CHECK_RETRO_PLANNING"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest]:
      t("orderItemStatus.info.cta.DECIDE_ON_MODIFICATION_REQUEST"),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief]: t(
      "orderItemStatus.info.cta.DECIDE_ON_PRE_MEDIA_BRIEF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition]:
      t("orderItemStatus.info.cta.DECIDE_ON_PRE_MEDIA_PROPOSITION"),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnProof]: t(
      "orderItemStatus.info.cta.DECIDE_ON_PROOF"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnQuote]: t(
      "orderItemStatus.info.cta.DECIDE_ON_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.SendQuote]: t(
      "orderItemStatus.info.cta.SEND_QUOTE"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.ShareRetroPlanning]: t(
      "orderItemStatus.info.cta.SHARE_RETRO_PLANNING"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryLabel]: t(
      "orderItemStatus.info.cta.ADD_DELIVERY_LABEL"
    ),
    [WorkflowPostponedActivityReadDtoActivityEnum.DecideOnDeliveryLabel]: t(
      "orderItemStatus.info.cta.DECIDE_ON_DELIVERY_LABEL"
    ),
  };

  const { isActive: isRetroplanningActive } = useGetIsFeatureActive(
    FeatureEnum.RETROPLANNING
  );

  const steps = useMemo(
    () =>
      formatOrderItemStepsFromHistory({
        actions: actions,
        orderItem: orderItem,
        isOMS,
        isRetroplanningActive,
      }),
    [actions, orderItem, isOMS, isRetroplanningActive]
  );

  const postponedActivitiesToShow = useMemo(() => {
    const workflowPostponedActivities =
      uniq(
        orderItem?.Workflow.PostponedActivities.map(
          (postponedActivity) => postponedActivity.activity
        )
      ) || [];

    const filteredActivities = workflowPostponedActivities.filter(
      (postponedActivity) =>
        (postponedActivity ===
          WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition &&
          isOMS) ||
        (steps.find((step) => step.activity === postponedActivity)?.action &&
          steps.find((step) => step.activity === postponedActivity)
            ?.isActionEnabled) ||
        (postponedActivity === "DECIDE_ON_QUOTE" && !isOMS) ||
        (postponedActivity === "DECIDE_ON_PROOF" && !isOMS) ||
        (postponedActivity === "DECIDE_ON_PRE_MEDIA_BRIEF" && isOMS) ||
        (postponedActivity === "ADD_PRE_MEDIA_PROPOSITION" && isOMS)
    );

    return sortBy(filteredActivities, (item) => activitiesOrder.indexOf(item));
  }, [orderItem, steps, isOMS]);

  const defaultSelectedActivity = useMemo(
    () =>
      postponedActivitiesToShow.length > 0
        ? postponedActivitiesToShow[0]
        : undefined,
    [postponedActivitiesToShow]
  );

  const currentSelectedActivity =
    selectedPostponedActivity ?? defaultSelectedActivity;

  // Handlers
  const handleSwitchStep = (params: { direction: number }) => () => {
    if (!currentSelectedActivity) {
      return;
    }
    const nextActivityIndex =
      postponedActivitiesToShow.indexOf(currentSelectedActivity) +
      params.direction;

    if (
      nextActivityIndex <= postponedActivitiesToShow.length - 1 &&
      nextActivityIndex >= 0
    ) {
      setSelectedPostponedActivity(
        postponedActivitiesToShow[nextActivityIndex]
      );
    }
  };

  const stepsTitleMapping = (
    activity:
      | WorkflowPostponedActivityReadDtoActivityEnum
      | "created"
      | undefined
  ) => {
    switch (activity) {
      case WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief:
      case WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition:
      case WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief:
      case WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition:
      case WorkflowPostponedActivityReadDtoActivityEnum.ProcessPreMediaProposition:
        return t("orderItemStatus.info.title.preMediaOptions");
      case WorkflowPostponedActivityReadDtoActivityEnum.AddQuote:
      case WorkflowPostponedActivityReadDtoActivityEnum.DecideOnQuote:
      case WorkflowPostponedActivityReadDtoActivityEnum.SendQuote:
        return t("orderItemStatus.info.title.definition");
      case WorkflowPostponedActivityReadDtoActivityEnum.AddHdf:
      case WorkflowPostponedActivityReadDtoActivityEnum.AddPurchaseOrder:
      case WorkflowPostponedActivityReadDtoActivityEnum.AddProof:
      case WorkflowPostponedActivityReadDtoActivityEnum.DecideOnProof:
        return t("orderItemStatus.info.title.production");
      case WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryNote:
        return t("orderItemStatus.info.title.delivery");
      case WorkflowPostponedActivityReadDtoActivityEnum.CheckRetroPlanning:
      case WorkflowPostponedActivityReadDtoActivityEnum.ShareRetroPlanning:
        return t("orderItemStatus.info.title.retroplanning");
      case WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest:
        return t("orderItemStatus.info.title.modificationRequest");
      case WorkflowPostponedActivityReadDtoActivityEnum.AddDeliveryLabel:
        return t("orderItemStatus.info.title.deliveryLabel");
      case WorkflowPostponedActivityReadDtoActivityEnum.DecideOnDeliveryLabel:
        return t("orderItemStatus.info.title.decideOnDeliveryLabel");
      default:
        return "";
    }
  };

  // UI
  if (isLoading || !orderItem || postponedActivitiesToShow?.length === 0) {
    return null;
  }

  const selectedStep = steps.find(
    (step) => step.activity === currentSelectedActivity
  );

  if (!currentSelectedActivity) {
    return null;
  }

  const activityIndex = postponedActivitiesToShow.indexOf(
    currentSelectedActivity
  );

  const sandboxItemProofId = (orderItem as OrderItemDetailsReadDto).sandboxItem
    ?.sandboxItemProof?.id;

  const preMediaRequiredClientActionToShow: boolean =
    [
      WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaBrief.toString(),
      WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaProposition.toString(),
    ].indexOf(selectedStep?.activity.toString() || "") !== -1 && !isOMS;

  const preMediaRequiredCSMActionToShow: boolean =
    [
      WorkflowPostponedActivityReadDtoActivityEnum.AddPreMediaProposition.toString(),
      WorkflowPostponedActivityReadDtoActivityEnum.DecideOnPreMediaBrief.toString(),
    ].indexOf(selectedStep?.activity.toString() || "") !== -1 && isOMS;

  return (
    <div className="flex w-full flex-col gap-4 rounded-br-sm !rounded-br-br-sm border border-slate-200 bg-white p-sp-md">
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-sp-xs">
          <RiFlashlightFill className="h-[24px] w-[24px] text-primary" />

          <div className="flex flex-col">
            <KdsText size="subtitle-xs" color="text-disabled">
              {t("orderItemStatus.info.step")}
            </KdsText>
            <KdsText size="subtitle-md">
              {stepsTitleMapping(selectedStep?.activity) ||
                (selectedStep?.activity &&
                  activityTitleMap[selectedStep.activity]) ||
                ""}
            </KdsText>
            <KdsText size="subtitle-xs" color="text-disabled">
              {selectedStep?.occuredAt &&
                dayjs(selectedStep?.occuredAt)?.format("L")}
            </KdsText>
          </div>
        </div>

        <div className="flex flex-row items-center gap-sp-xs font-ff-body">
          <KdsText color="text-bodyvariant">
            {activityIndex + 1} / {postponedActivitiesToShow.length}
          </KdsText>

          {postponedActivitiesToShow.length > 1 && (
            <KdsButtonIcon
              color="neutral"
              disabled={activityIndex <= 0}
              variant="outlined"
              onPress={handleSwitchStep({ direction: -1 })}
              icon={RiArrowLeftSLine}
              size="small"
            />
          )}

          {postponedActivitiesToShow.length > 1 && (
            <KdsButtonIcon
              disabled={activityIndex >= postponedActivitiesToShow.length - 1}
              color="neutral"
              variant="outlined"
              onPress={handleSwitchStep({ direction: 1 })}
              icon={RiArrowRightSLine}
              size="small"
            />
          )}
        </div>
      </div>

      {((selectedStep?.activity === "DECIDE_ON_QUOTE" && !isOMS) ||
        (selectedStep?.activity === "DECIDE_ON_PROOF" && !isOMS) ||
        preMediaRequiredClientActionToShow ||
        preMediaRequiredCSMActionToShow ||
        selectedStep?.action) && (
        <div className="flex w-full flex-col items-center">
          <div className="flex w-full flex-col gap-sp-md rounded-md border p-sp-md outline-outlined-border">
            <KdsText>
              {(selectedStep?.activity &&
                activityDescriptionMap[selectedStep.activity]) ||
                ""}
            </KdsText>

            {preMediaRequiredCSMActionToShow && (
              <OrderItemCSMPreMediaSection orderItem={orderItem} />
            )}

            {selectedStep?.activity === "DECIDE_ON_QUOTE" &&
              (orderItem as OrderItemDetailsReadDto).sandboxItem
                ?.SandboxItemQuote.length > 0 && (
                <QuoteValidationActions
                  orderId={orderItem.orderId}
                  orderItemId={orderItem.id}
                  quotes={
                    (orderItem as OrderItemDetailsReadDto).sandboxItem
                      ?.SandboxItemQuote
                  }
                  onError={onError}
                />
              )}

            {selectedStep?.activity === "DECIDE_ON_PROOF" &&
              sandboxItemProofId &&
              (orderItem as OrderItemDetailsReadDto).sandboxItem
                .sandboxItemProof && (
                <ProofValidationActions
                  key={`sandboxItem-${(orderItem as OrderItemDetailsReadDto).sandboxItem?.id}`}
                  files={
                    (orderItem as OrderItemDetailsReadDto).sandboxItem
                      .sandboxItemProof?.files || []
                  }
                  sandboxItemProofId={sandboxItemProofId}
                  onError={onError}
                  orderItem={orderItem as OrderItemDetailsReadDto}
                  versions={
                    (
                      orderItem as OrderItemDetailsReadDto
                    ).sandboxItem?.SandboxItemVersion.map((v) => ({
                      versionId: v.id,
                      versionName: v.name,
                    })) || []
                  }
                />
              )}

            <div className="flex w-full justify-end">
              {selectedStep?.action && (
                <KdsButton
                  size="small"
                  onPress={() => selectedStep?.action?.(orderItem)}
                  dataCy={`action-${selectedStep?.activity}`}
                >
                  {(selectedStep?.activity &&
                    activityCtaMap[selectedStep.activity]) ||
                    ""}
                </KdsButton>
              )}
            </div>
          </div>
          <div className="mt-[1px] w-full px-2">
            <div className="h-1 w-full rounded-md bg-neutral-light" />
          </div>
        </div>
      )}
    </div>
  );
};

export const OrderItemStatus = withOrderItemActions(OrderItemStatusComponent, {
  showDecideOnQuoteButton: false,
  showDecideOnProofButton: false,
  showDecideOnPreMediaBriefButton: false,
  showAddPreMediaPropositionButton: false,
  showDecideOnModificationRequestButton: true,
});
